import { useState } from "react";
import { Check, Sparkles, Lock, Layers, Gamepad2 } from "lucide-react";

// STAGE 6 · PHASE 1 — Gateway Interceptor.
// Slides up on landing mount when the authed user has no module profile array.
// Purely client-side (localStorage) per Phase-1 constraints — no MongoDB writes.

export const ECOSYSTEM_MODULES = [
  { key: "vault", label: "Secure Vault", desc: "PIN-locked private storage", Icon: Lock, accent: "#D4AF37" },
  { key: "profile", label: "Profile", desc: "Identity & layout", Icon: Layers, accent: "#94A3B8" },
  { key: "leisure", label: "Leisure", desc: "Habits, hobbies, play", Icon: Layers, accent: "#94A3B8" },
  { key: "career", label: "Career", desc: "Milestones & portfolio", Icon: Layers, accent: "#94A3B8" },
  { key: "wealth", label: "Wealth & Money", desc: "Tokens & budgeting", Icon: Layers, accent: "#94A3B8" },
  { key: "business", label: "Business & Tools", desc: "Productivity engines", Icon: Layers, accent: "#94A3B8" },
  { key: "creations", label: "Mist Creations", desc: "AI studios & pipelines", Icon: Sparkles, accent: "#A78BFA" },
  { key: "games", label: "Games", desc: "Casual gaming ecosystem", Icon: Gamepad2, accent: "#818CF8" },
];

export default function ModuleOnboard({ onComplete }) {
  const [selected, setSelected] = useState(() => new Set(ECOSYSTEM_MODULES.map((m) => m.key)));

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const confirm = () => onComplete(Array.from(selected));

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="module-onboard-overlay">
      <div className="w-[328px] max-w-[calc(100vw-32px)] rounded-3xl border-4 border-indigo-500 bg-slate-950 p-5 pb-6 animate-[slideUp_.35s_ease-out]"
        style={{ boxShadow: "0 8px 40px rgba(99,102,241,0.25)" }}>
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl border-2 border-indigo-400 bg-indigo-500/15">
            <Sparkles size={18} className="text-indigo-300" />
          </span>
          <div className="leading-tight">
            <h2 className="font-serif text-xl text-white" data-testid="module-onboard-title">Configure Your Ecosystem</h2>
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-indigo-300/70">Pick the modules you want on your deck</p>
          </div>
        </div>

        <div className="mt-4 grid max-h-[42vh] grid-cols-2 gap-2 overflow-y-auto no-scrollbar pr-0.5">
          {ECOSYSTEM_MODULES.map((m) => {
            const on = selected.has(m.key);
            const Icon = m.Icon;
            return (
              <button
                key={m.key}
                onClick={() => toggle(m.key)}
                data-testid={`module-onboard-opt-${m.key}`}
                className={[
                  "relative flex flex-col items-start gap-1.5 rounded-2xl border-2 p-3 text-left transition-colors",
                  on ? "border-indigo-400 bg-indigo-500/10" : "border-white/10 bg-white/[0.03]",
                ].join(" ")}
              >
                <span className="grid h-8 w-8 place-items-center rounded-lg border-2"
                  style={{ borderColor: on ? m.accent : "rgba(255,255,255,0.15)" }}>
                  <Icon size={15} style={{ color: on ? m.accent : "rgba(255,255,255,0.4)" }} />
                </span>
                <span className="font-sans text-[13px] font-bold leading-tight text-white">{m.label}</span>
                <span className="font-mono text-[8px] uppercase tracking-wide text-white/40">{m.desc}</span>
                {on && (
                  <span className="absolute right-2 top-2 grid h-4 w-4 place-items-center rounded-full bg-indigo-400">
                    <Check size={11} strokeWidth={3.5} className="text-slate-950" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={confirm}
          disabled={selected.size === 0}
          data-testid="module-onboard-confirm-btn"
          className="mt-4 w-full rounded-full border-2 border-indigo-400 bg-gradient-to-r from-indigo-600 to-purple-600 py-3.5 font-mono text-[11px] font-black uppercase tracking-[0.2em] text-white shadow-[4px_4px_0px_#312E81] transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40"
        >
          Enter Misteck · {selected.size} Modules
        </button>
      </div>
    </div>
  );
}
